import { Injectable } from '@angular/core';
import { Ki67PointsWithData } from '@core/models/ki67points-with-data.model';
import { Ki67Points } from '@core/models/ki67points.model';
import { Ki67Status } from '@core/models/ki67status.model';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class Ki67Service {
  ki67DataByAnnotation = new Map();
  newKi67Data = new BehaviorSubject<Ki67Status>(new Ki67Status());

  processPointArray(rawArray: any[]) {
    return rawArray.map((r) => new Ki67PointsWithData(r[0], r[1], r[2], r[3]));
  }

  computeIndex(rawStatus: Ki67Status) {
    const filteredPositivePoints = rawStatus.positivePoints.filter(
      (p) =>
        p.intensity >= rawStatus.minIntensity && p.size >= rawStatus.minSize,
    );
    const filteredNegativePoints = rawStatus.negativePoints.filter(
      (p) =>
        p.intensity >= rawStatus.minIntensity && p.size >= rawStatus.minSize,
    );

    if (filteredPositivePoints.length + filteredNegativePoints.length > 0) {
      rawStatus.ki67Index =
        100.0 *
        (filteredPositivePoints.length /
          (filteredPositivePoints.length + filteredNegativePoints.length));
    }
  }

  updateStatus(
    status: Ki67Points | null,
    imageId: number,
    annotationId: number,
    requestId: string,
  ) {
    if (status == null) {
      const cancelData = {
        imageId: imageId,
        annotationId: annotationId,
        requisitionId: requestId,
        positivePoints: [],
        negativePoints: [],
        ki67Index: -1,
        minIntensity: -Infinity,
        minSize: -Infinity,
      };

      const key = `${imageId}_${annotationId}`;
      this.ki67DataByAnnotation.set(key, cancelData);
      this.newKi67Data.next(cancelData);
      return;
    }
    if (
      status.positive_points === undefined ||
      status.positive_points === null
    ) {
      status.positive_points = [];
    }

    if (
      status.negative_points === undefined ||
      status.negative_points === null
    ) {
      status.negative_points = [];
    }

    const key = `${imageId}_${annotationId}`;
    if (this.ki67DataByAnnotation.has(key)) {
      if (this.ki67DataByAnnotation.get(key).requisitionId !== requestId) {
        const newData = new Ki67Status();
        newData.annotationId = annotationId;
        newData.requisitionId = requestId;
        newData.imageId = imageId;
        newData.positivePoints = this.processPointArray(status.positive_points);
        newData.negativePoints = this.processPointArray(status.negative_points);
        this.computeIndex(newData);
        this.ki67DataByAnnotation.set(key, newData);
        this.newKi67Data.next(newData);
      } else {
        const currentData = this.ki67DataByAnnotation.get(key);
        currentData.positivePoints = currentData.positivePoints.concat(
          this.processPointArray(status.positive_points),
        );
        currentData.negativePoints = currentData.negativePoints.concat(
          this.processPointArray(status.negative_points),
        );
        this.computeIndex(currentData);
        this.newKi67Data.next(currentData);
      }
    } else {
      const newData = new Ki67Status();
      newData.annotationId = annotationId;
      newData.requisitionId = requestId;
      newData.imageId = imageId;
      newData.positivePoints = this.processPointArray(status.positive_points);
      newData.negativePoints = this.processPointArray(status.negative_points);
      this.computeIndex(newData);
      this.ki67DataByAnnotation.set(key, newData);
      this.newKi67Data.next(newData);
    }
  }

  updateMinIntensity(
    imageId: number,
    annotationId: number,
    minIntensity: number,
  ) {
    const key = `${imageId}_${annotationId}`;
    const toChange = this.ki67DataByAnnotation.get(key);
    toChange.minIntensity = minIntensity;
    this.computeIndex(toChange);
    this.newKi67Data.next(toChange);
  }

  updateMinSize(imageId: number, annotationId: number, minSize: number) {
    const key = `${imageId}_${annotationId}`;
    const toChange = this.ki67DataByAnnotation.get(key);
    toChange.minSize = minSize;
    this.computeIndex(toChange);
    this.newKi67Data.next(toChange);
  }

  getForAnnotation(imageId: number, annotationId: number) {
    const key = `${imageId}_${annotationId}`;
    if (this.ki67DataByAnnotation.has(key)) {
      return this.ki67DataByAnnotation.get(key);
    }
    return null;
  }
}
